'use client';

import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { useActiveAccountId } from '@frameio/shared/contexts';
import { useProjectDetailsFragment } from '../../project/gql/generated/ProjectDetails';
import {
  DuplicateProjectModal,
  type DuplicateProjectModalProps,
} from '../components/DuplicateProjectModal';

export type ShowDuplicateProjectModalInput = {
  onRequestClose?: () => void;
  projectId: string;
};

export type UseDuplicateProjectModalReturn = {
  duplicateProjectModal: React.ReactNode;
  showDuplicateProjectModal: (input: ShowDuplicateProjectModalInput) => void;
  hideDuplicateProjectModal: () => void;
};

export const useDuplicateProjectModal = (): UseDuplicateProjectModalReturn => {
  const accountId = useActiveAccountId();
  const [props, setProps] = useState<DuplicateProjectModalProps>({
    accountId,
    onRequestClose: noop,
    isShowing: false,
    workspaceId: '',
    projectId: '',
  });

  const { data } = useProjectDetailsFragment(props.projectId);
  const workspaceId = useMemo(() => data?.workspace?.id ?? '', [data]);

  const duplicateProjectModal = useMemo(
    () => <DuplicateProjectModal {...props} workspaceId={workspaceId} />,
    [props, workspaceId]
  );

  const hideDuplicateProjectModal = useCallback(() => {
    setProps((prev) => ({
      ...prev,
      isShowing: false,
      onRequestClose: noop,
      projectId: '',
    }));
  }, []);

  const showDuplicateProjectModal = useCallback(
    ({
      onRequestClose = hideDuplicateProjectModal,
      projectId,
    }: ShowDuplicateProjectModalInput) => {
      setProps((prev) => ({
        ...prev,
        isShowing: true,
        onRequestClose,
        projectId,
      }));
    },
    [hideDuplicateProjectModal]
  );

  return {
    duplicateProjectModal,
    showDuplicateProjectModal,
    hideDuplicateProjectModal,
  };
};
